// BYOPEdit.js
//
// Post-import EDIT view for a BYOP pipeline: change inputs/parameters (kind, type, default,
// options, required, flag, extensions) and the container (Dockerfile or image URL). Mirrors
// the import wizard's Step 2 + Step 3 editors but operates on an ALREADY-COMPLETE pipeline
// via POST /byop/edit/:chatId. Changing the Dockerfile re-triggers container validation;
// editing only inputs/params leaves the container (and launchability) untouched.
import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE from "./config";
import "./byop/ImportWizard.css";

const toRows = (data) => [
    ...((data.input_files || [])).map((x) => ({
        kind: "input", name: x.name || "", flag: x.flag || (x.name ? "--" + x.name : ""),
        required: !!x.required, type: "string", default: "",
        choices: "", extensions: Array.isArray(x.extensions) ? x.extensions.join(",") : (x.extensions || ""),
        description: x.description || "",
    })),
    ...((data.parameters || [])).map((x) => ({
        kind: "param", name: x.name || "", flag: x.flag || (x.name ? "--" + x.name : ""),
        required: !!x.required, alwaysInclude: !!x.alwaysInclude, type: x.type || "string", default: x.default == null ? "" : String(x.default),
        choices: Array.isArray(x.choices) ? x.choices.join(",") : (x.choices || ""), extensions: "",
        description: x.description || "",
    })),
];

export default function BYOPEdit({ chatId }) {
    const [data, setData] = useState(null);
    const [rows, setRows] = useState([]);
    const [dockerMode, setDockerMode] = useState("none"); // 'none' (leave unchanged) | 'image'
    const [imageUrl, setImageUrl] = useState("");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/byop-workflow/${chatId}`);
                if (cancelled) return;
                setData(res.data);
                setRows(toRows(res.data));
                setImageUrl(res.data.docker_image_uri || "");
                setDockerMode(res.data.docker_image_uri ? "image" : "none");
            } catch (e) {
                if (!cancelled) setError("Could not load this pipeline.");
            }
        })();
        return () => { cancelled = true; };
    }, [chatId]);

    const upd = (i, k, v) => setRows((a) => a.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
    const addRow = (kind) => setRows((a) => [...a, { kind, name: "", flag: "", required: false, alwaysInclude: false, type: "string", default: "", choices: "", extensions: "", description: "" }]);
    const removeRow = (i) => setRows((a) => a.filter((_, idx) => idx !== i));

    const split = () => ({
        inputs: rows.filter((r) => r.kind === "input").map((r) => ({
            name: r.name, flag: r.flag, required: r.required,
            extensions: String(r.extensions || "").split(",").map((s) => s.trim()).filter(Boolean),
            description: r.description,
        })),
        parameters: rows.filter((r) => r.kind === "param").map((r) => ({
            name: r.name, flag: r.flag, type: r.type, default: r.default, required: r.required, alwaysInclude: !!r.alwaysInclude, description: r.description,
            choices: String(r.choices || "").split(",").map((s) => s.trim()).filter(Boolean),
        })),
    });

    const save = async () => {
        setError(""); setNotice("");
        const s = split();
        if (s.inputs.concat(s.parameters).some((x) => !x.name || !x.flag)) { setError("Every row needs a name and a flag."); return; }
        for (const p of s.parameters) {
            if (p.type === "enum") {
                if (!p.choices.length) { setError(`Parameter "${p.name}": enter comma-separated options for the enum.`); return; }
                if (p.default !== "" && !p.choices.map(String).includes(String(p.default))) { setError(`Parameter "${p.name}": default must be one of the options.`); return; }
            }
            if (p.type === "list") {
                if (!p.choices.length) { setError(`Parameter "${p.name}": enter comma-separated options for the list.`); return; }
                const toks = String(p.default || "").split(",").map((t) => t.trim()).filter(Boolean);
                if (toks.some((t) => !p.choices.map(String).includes(t))) { setError(`Parameter "${p.name}": each list default must be one of the options.`); return; }
            }
        }
        const body = { inputs: s.inputs, parameters: s.parameters };
        if (dockerMode === "image") { if (!imageUrl.trim()) { setError("Enter a container image URL."); return; } body.imageUrl = imageUrl.trim(); }
        setBusy(true);
        try {
            const res = await axios.post(`${API_BASE}/byop/edit/${chatId}`, body, { withCredentials: true });
            setNotice(res.data.rebuilding
                ? "✓ Saved. The container changed — it's re-validating (rebuild) and can't be launched until that passes."
                : "✓ Saved. Your changes take effect on the next launch.");
        } catch (e) {
            setError(e.response?.data?.error || "Failed to save changes.");
        } finally { setBusy(false); }
    };

    if (error && !data) return <p className="workflow-error-text">{error}</p>;
    if (!data) return <p className="workflow-loading-text">Loading pipeline…</p>;

    return (
        <div className="iw-root" style={{ padding: 8 }}>
            <div className="iw-h">Edit pipeline — {data.short_name || "BYOP"}</div>
            <div className="iw-sub">Adjust inputs and parameters (kind, type, default, options) and the container image. Inputs are files chosen at launch; parameters are values.</div>

            {error && <div className="iw-note err">{error}</div>}
            {notice && <div className="iw-note ok">{notice}</div>}

            <div className="iw-h" style={{ marginTop: 16 }}>Inputs &amp; parameters</div>
            <table className="iw-table">
                <thead><tr><th>Kind</th><th>Name</th><th>Flag</th><th>Required</th><th title="Always include this parameter in the launch command, even at its default value (for a boolean, always emit the flag)">Add to Command</th><th>Details</th><th></th></tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td>
                                <select className="iw-select" value={r.kind} onChange={(e) => upd(i, "kind", e.target.value)}>
                                    <option value="input">Input (file)</option>
                                    <option value="param">Parameter</option>
                                </select>
                            </td>
                            <td><input className="iw-input" value={r.name} onChange={(e) => upd(i, "name", e.target.value)} /></td>
                            <td><input className="iw-input" value={r.flag} onChange={(e) => upd(i, "flag", e.target.value)} placeholder={"--" + (r.name || "flag")} /></td>
                            <td style={{ textAlign: "center" }}><input type="checkbox" checked={!!r.required} onChange={(e) => upd(i, "required", e.target.checked)} /></td>
                            <td style={{ textAlign: "center" }}>
                                {r.kind === "param"
                                    ? <input type="checkbox" checked={!!r.alwaysInclude} onChange={(e) => upd(i, "alwaysInclude", e.target.checked)} title="Always include in the launch command" />
                                    : <span style={{ color: "#cbd5e1" }}>—</span>}
                            </td>
                            <td>
                                {r.kind === "param" ? (
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                        <select className="iw-select" style={{ maxWidth: 100 }} value={r.type} onChange={(e) => upd(i, "type", e.target.value)}>
                                            {["string", "number", "boolean", "enum", "list", "path"].map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        {(r.type === "enum" || r.type === "list") && (
                                            <input className="iw-input" style={{ maxWidth: 150 }} value={r.choices || ""} onChange={(e) => upd(i, "choices", e.target.value)} placeholder="options e.g. Y,N" />
                                        )}
                                        {r.type !== "boolean" && (
                                            <input className="iw-input" value={r.default} onChange={(e) => upd(i, "default", e.target.value)} placeholder={r.type === "enum" ? "default (one of options)" : r.type === "list" ? "default e.g. a,b,c" : r.type === "path" ? "https://… or s3://…" : "default value"} />
                                        )}
                                    </div>
                                ) : (
                                    <input className="iw-input" value={r.extensions || ""} onChange={(e) => upd(i, "extensions", e.target.value)} placeholder="extensions e.g. fastq,gz" />
                                )}
                            </td>
                            <td><button className="iw-btn ghost sm" onClick={() => removeRow(i)}>✕</button></td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={7} style={{ color: "#94a3b8" }}>No inputs/parameters — add some below.</td></tr>}
                </tbody>
            </table>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button className="iw-btn ghost" onClick={() => addRow("input")}>+ Add input</button>
                <button className="iw-btn ghost" onClick={() => addRow("param")}>+ Add parameter</button>
            </div>

            <div className="iw-h" style={{ marginTop: 20 }}>Container</div>
            <div className="iw-tabs">
                <div className={`iw-tab ${dockerMode === "none" ? "active" : ""}`} onClick={() => setDockerMode("none")}>Leave unchanged / none</div>
                <div className={`iw-tab ${dockerMode === "image" ? "active" : ""}`} onClick={() => setDockerMode("image")}>Image URL</div>
            </div>
            {dockerMode === "none" ? (
                <div className="iw-note info">The container is left as-is. {data.docker_image_uri ? `This pipeline uses image: ${data.docker_image_uri}` : "This pipeline declares its own per-process containers (no image needed)."}</div>
            ) : (
                <div className="iw-field">
                    <label>Container image URL</label>
                    <input className="iw-input" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="quay.io/biocontainers/samtools:1.19--h50ea8bc_0" />
                    <div className="iw-note warn">The image must be publicly pullable (or, for ECR, accessible to the CloudGenomics compute role). It is used as-is — not built or scanned.</div>
                </div>
            )}

            <div className="iw-actions" style={{ marginTop: 18 }}>
                <button className="iw-btn primary" onClick={save} disabled={busy}>{busy ? <span className="iw-spin" /> : "Save changes"}</button>
            </div>
        </div>
    );
}
